import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import {
  Shield, Users, Brain, Ban, Lock, Calendar, FileText,
  ScrollText, Eye, Trash2, PenTool, Mail, ChevronRight,
  Check, X, Building2, ArrowRight
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function DocumentModal({ doc, onClose }) {
  if (!doc) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#0a1729]/70 backdrop-blur-sm p-4" data-testid="doc-modal">
      <div className="bg-slate-900 border border-slate-700 rounded-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-white">{doc.title}</h3>
          <button onClick={onClose} className="text-slate-300 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-4 text-sm">
          {doc.sections?.map((sec, i) => (
            <div key={i}>
              <h4 className="font-semibold text-white mb-1">{sec.heading}</h4>
              <p className="text-slate-300 whitespace-pre-line leading-relaxed">{sec.content}</p>
            </div>
          ))}
          {doc.key_terms?.map((term, i) => (
            <p key={i} className="text-slate-300">{i + 1}. {term}</p>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function TrustPage() {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [selectedDoc, setSelectedDoc] = useState(null);

  useEffect(() => {
    axios.get(`${API}/compliance/all-templates`)
      .then(res => setTemplates(res.data.templates || []))
      .catch(() => {});
  }, []);

  const trustBadges = [
    { icon: Shield, label: 'FERPA Compliant', color: 'text-blue-400', bg: 'bg-blue-500/10' },
    { icon: Users, label: 'Parent-First Registration', color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
    { icon: Brain, label: 'AI Transparent', color: 'text-purple-400', bg: 'bg-purple-500/10' },
    { icon: Ban, label: 'No Ads Ever', color: 'text-red-400', bg: 'bg-red-500/10' },
    { icon: Lock, label: 'Data Never Sold', color: 'text-amber-400', bg: 'bg-amber-500/10' },
    { icon: Calendar, label: '14+ Only', color: 'text-cyan-400', bg: 'bg-cyan-500/10' },
  ];

  const dataCollected = [
    { what: 'Full name & grade level', why: 'Personalize the student experience', retention: 'Until account deletion' },
    { what: 'Academic documents', why: 'AI-powered portfolio analysis', retention: 'Until account deletion' },
    { what: 'Test scores & GPA', why: 'Profile rating & gap analysis', retention: 'Until account deletion' },
    { what: 'Career interests', why: 'Personalized career recommendations', retention: 'Until account deletion' },
    { what: 'Activity & awards', why: 'Extracurricular profile strength', retention: 'Until account deletion' },
    { what: 'AI chat history', why: 'Continuous advisor context', retention: '12 months after last interaction' },
    { what: 'Parent email', why: 'Authorization & notifications', retention: 'Until account deletion' },
    { what: 'Usage analytics', why: 'Platform improvement', retention: 'Aggregated, anonymized' },
  ];

  const rights = [
    { icon: Eye, title: 'See Your Data', desc: 'Access all data we have about you or your child at any time from your dashboard.' },
    { icon: PenTool, title: 'Correct Your Data', desc: 'Request correction of any inaccurate information. We respond within 30 business days.' },
    { icon: Trash2, title: 'Delete Everything', desc: 'Request complete deletion of all data. Permanently removed within 30 days.' },
    { icon: X, title: 'Withdraw Consent', desc: 'Parents can withdraw consent at any time, triggering the 30-day deletion process.' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
      {/* Nav */}
      <nav className="sticky top-0 z-40 bg-[#0a1729]/80 backdrop-blur-md border-b border-slate-800 px-6 py-4">
        <div className="max-w-5xl mx-auto flex justify-between items-center">
          <button onClick={() => navigate('/')} className="text-xl font-bold text-white">Agentic FuturePath AI</button>
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/login')} className="text-sm text-slate-300 hover:text-white transition-colors">Sign In</button>
            <Button size="sm" onClick={() => navigate('/pricing')} data-testid="trust-register-btn">Get Started</Button>
          </div>
        </div>
      </nav>

      <div className="max-w-5xl mx-auto px-6 py-16 space-y-20">
        {/* Hero */}
        <section className="text-center space-y-4" data-testid="trust-hero">
          <h1 className="text-4xl sm:text-5xl font-bold text-white leading-tight">
            Built for Students.<br /><span className="text-blue-400">Trusted by Parents.</span>
          </h1>
          <p className="text-lg text-slate-300 max-w-2xl mx-auto">
            We believe families should know exactly how their data is used. No surprises, no fine print you need a lawyer to understand.
          </p>
        </section>

        {/* Trust Badges */}
        <section className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4" data-testid="trust-badges">
          {trustBadges.map((badge, i) => (
            <div key={i} className={`${badge.bg} rounded-xl p-4 text-center border border-slate-700/50`}>
              <badge.icon className={`w-6 h-6 ${badge.color} mx-auto mb-2`} />
              <span className="text-xs font-medium text-white">{badge.label}</span>
            </div>
          ))}
        </section>

        {/* How Registration Works */}
        <section data-testid="trust-registration-flow">
          <h2 className="text-2xl font-bold text-white text-center mb-8">How Registration Works</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { step: '1', icon: Users, title: 'Parent Creates Account', desc: 'Parent registers with their email and enters their child\'s information. Age is verified server-side.' },
              { step: '2', icon: FileText, title: 'Parent Signs Authorization', desc: 'Parent reviews and signs 4 compliance documents: Parental Consent, FERPA Rights, AI Disclosure, and Terms.' },
              { step: '3', icon: Check, title: 'Student Activates', desc: 'Student receives an activation email, sets their password, and starts exploring. All compliance is pre-handled.' },
            ].map((item, i) => (
              <div key={i} className="relative bg-slate-800/50 border border-slate-700 rounded-xl p-6 text-center">
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-white font-bold text-sm">{item.step}</div>
                <item.icon className="w-8 h-8 text-blue-400 mx-auto mt-3 mb-3" />
                <h3 className="text-white font-semibold mb-2">{item.title}</h3>
                <p className="text-sm text-slate-300">{item.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* The 4 Documents You Sign */}
        <section data-testid="trust-documents">
          <h2 className="text-2xl font-bold text-white text-center mb-2">The 4 Documents You Sign</h2>
          <p className="text-slate-300 text-center mb-8">Every document is transparent and readable. No legalese.</p>
          <div className="grid sm:grid-cols-2 gap-4">
            {templates.filter(t => t.document_type !== 'school_dpa').map((tmpl, i) => {
              const icons = { parental_consent: FileText, ferpa_acknowledgment: ScrollText, ai_processing_disclosure: Brain, terms_acceptance: Lock };
              const colors = { parental_consent: 'text-blue-400', ferpa_acknowledgment: 'text-purple-400', ai_processing_disclosure: 'text-cyan-400', terms_acceptance: 'text-amber-400' };
              const Icon = icons[tmpl.document_type] || FileText;
              const color = colors[tmpl.document_type] || 'text-blue-400';
              return (
                <div key={i} className="bg-slate-800/50 border border-slate-700 rounded-xl p-5">
                  <Icon className={`w-6 h-6 ${color} mb-3`} />
                  <h3 className="text-white font-semibold mb-1">{tmpl.title}</h3>
                  <p className="text-sm text-slate-300 mb-3">
                    {tmpl.sections?.[0]?.content?.substring(0, 120)}...
                  </p>
                  <button onClick={() => setSelectedDoc(tmpl)} className="text-sm text-blue-400 hover:text-blue-300 flex items-center gap-1" data-testid={`view-${tmpl.document_type}`}>
                    View Sample <ChevronRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              );
            })}
          </div>
        </section>

        {/* What Data We Collect */}
        <section data-testid="trust-data-collection">
          <h2 className="text-2xl font-bold text-white text-center mb-8">What Data We Collect</h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {dataCollected.map((item, i) => (
              <div key={i} className="flex items-start gap-3 bg-slate-800/30 rounded-lg p-4 border border-slate-800">
                <Check className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" />
                <div>
                  <p className="text-sm text-white font-medium">{item.what}</p>
                  <p className="text-xs text-slate-300">{item.why}</p>
                  <p className="text-xs text-slate-300 mt-1">Kept: {item.retention}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* How AI Works */}
        <section className="bg-slate-800/30 border border-slate-700 rounded-xl p-8" data-testid="trust-ai-section">
          <div className="flex items-center gap-3 mb-4">
            <Brain className="w-8 h-8 text-purple-400" />
            <h2 className="text-2xl font-bold text-white">How AI Works on This Platform</h2>
          </div>
          <div className="space-y-4 text-slate-300">
            <p>Agentic FuturePath AI uses <strong className="text-white">Claude</strong>, made by <strong className="text-white">Anthropic</strong>, to analyze academic documents your child uploads.</p>
            <p>When a document is uploaded, Claude reads the text and extracts structured data: GPA, course grades, test scores, and achievements.</p>
            <div className="bg-slate-900/50 rounded-lg p-4 border border-slate-700">
              <p className="text-sm font-semibold text-white mb-2">The Human Confirmation Step</p>
              <p className="text-sm">Every time AI extracts data, the student sees what was found and must <strong className="text-emerald-400">manually confirm</strong> before anything is saved. Nothing happens automatically. The student always has the final say.</p>
            </div>
            <p className="text-sm text-slate-300">Claude does not retain your child's data. It does not train on your child's information. Each analysis is independent and stateless.</p>
          </div>
        </section>

        {/* FERPA Rights */}
        <section data-testid="trust-ferpa">
          <h2 className="text-2xl font-bold text-white text-center mb-2">Your FERPA Rights</h2>
          <p className="text-slate-300 text-center mb-6">In plain English, what you and your child can do.</p>
          <div className="bg-slate-800/30 border border-slate-700 rounded-xl p-6 space-y-3">
            <p className="text-slate-300 text-sm"><strong className="text-white">Inspect records:</strong> You can view all educational records stored on this platform at any time through your parent dashboard.</p>
            <p className="text-slate-300 text-sm"><strong className="text-white">Request corrections:</strong> If anything is inaccurate, email us or use the Privacy Center. We respond within 30 business days.</p>
            <p className="text-slate-300 text-sm"><strong className="text-white">Request deletion:</strong> You can request all educational records be permanently deleted. We comply within 30 days.</p>
            <p className="text-slate-300 text-sm"><strong className="text-white">At age 18:</strong> FERPA rights transfer from the parent to the student. Your child takes full control.</p>
          </div>
        </section>

        {/* For Schools */}
        <section className="bg-slate-800/30 border border-slate-700 rounded-xl p-8" data-testid="trust-schools">
          <div className="flex items-center gap-3 mb-4">
            <Building2 className="w-8 h-8 text-cyan-400" />
            <h2 className="text-2xl font-bold text-white">For Schools & Organizations</h2>
          </div>
          <p className="text-slate-300 mb-4">Educational institutions can license Agentic FuturePath AI for their students. Before any student data enters the platform, the school must sign a <strong className="text-white">Data Processing Agreement (DPA)</strong>.</p>
          <p className="text-slate-300 text-sm mb-4">The DPA covers FERPA compliance, data security, breach notification, data retention, and deletion terms. Once signed, the institution has taken legal responsibility for its students.</p>
          <a href="mailto:partnerships@futurepathAI.com" className="inline-flex items-center gap-2 text-cyan-400 hover:text-cyan-300 text-sm font-medium">
            Contact us to request a DPA <ArrowRight className="w-4 h-4" />
          </a>
        </section>

        {/* Your Rights */}
        <section data-testid="trust-rights">
          <h2 className="text-2xl font-bold text-white text-center mb-8">Your Rights</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {rights.map((right, i) => (
              <div key={i} className="bg-slate-800/50 border border-slate-700 rounded-xl p-5 flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center shrink-0">
                  <right.icon className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-1">{right.title}</h3>
                  <p className="text-sm text-slate-300">{right.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Contact */}
        <section className="text-center bg-slate-800/30 border border-slate-700 rounded-xl p-8" data-testid="trust-contact">
          <Mail className="w-8 h-8 text-blue-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">Questions? We're Here.</h2>
          <p className="text-slate-300 mb-4">Email us at <a href="mailto:privacy@futurepathAI.com" className="text-blue-400 hover:underline">privacy@futurepathAI.com</a></p>
          <p className="text-slate-300 text-sm">We respond to all privacy inquiries within 30 business days.</p>
          <div className="flex justify-center gap-4 mt-6 text-sm">
            <a href="/terms" className="text-slate-300 hover:text-white">Terms of Service</a>
            <span className="text-slate-300">|</span>
            <a href="/trust" className="text-slate-300 hover:text-white">Privacy Policy</a>
          </div>
        </section>
      </div>

      {/* Document modal */}
      {selectedDoc && <DocumentModal doc={selectedDoc} onClose={() => setSelectedDoc(null)} />}
    </div>
  );
}
